import { useCallback } from 'react';
import { shouldSkipTracking, logDebugInfo } from '@/lib/environment';

declare global {
  interface Window {
    fbq: any;
    gtag: any;
  }
}

export const useMetaPixel = () => {
  const trackEvent = useCallback((eventName: string, parameters?: Record<string, any>) => {
    if (shouldSkipTracking()) {
      logDebugInfo(`Meta Pixel Event (SKIPPED): ${eventName}`, parameters);
      return;
    }

    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', eventName, parameters);
    }
  }, []);

  const trackCustomEvent = useCallback((eventName: string, parameters?: Record<string, any>) => {
    if (shouldSkipTracking()) {
      logDebugInfo(`Meta Pixel Custom Event (SKIPPED): ${eventName}`, parameters);
      return;
    }

    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('trackCustom', eventName, parameters);
    }
  }, []);

  // Google Analytics tracking
  const trackGAEvent = useCallback((eventName: string, parameters?: Record<string, any>) => {
    if (shouldSkipTracking()) {
      logDebugInfo(`Google Analytics Event (SKIPPED): ${eventName}`, parameters);
      return;
    }

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, parameters);
    }
  }, []);

  // Eventos específicos para o negócio
  const trackLead = useCallback((formData?: Record<string, any>) => {
    if (shouldSkipTracking()) {
      logDebugInfo('Lead Conversion (SKIPPED)', {
        formData,
        reason: 'Development environment detected'
      });
      return;
    }
    // Calcular valor estimado baseado no faturamento informado
    const getLeadValue = (revenue: string) => {
      const revenueMap: Record<string, number> = {
        'Até R$ 50 mil/mês': 500,
        'De R$ 50 mil a R$ 100 mil/mês': 1000,
        'De R$ 100 mil a R$ 300 mil/mês': 2000,
        'De R$ 300 mil a R$ 1 milhão/mês': 5000,
        'Acima de R$ 1 milhão/mês': 10000
      };
      return revenueMap[revenue] || 1000;
    };

    const leadValue = formData?.revenue ? getLeadValue(formData.revenue) : 1000;

    // Meta Pixel - Evento Lead com valor e parâmetros completos
    trackEvent('Lead', {
      content_name: 'Formulário de Contato Scale IA',
      content_category: 'Lead Generation',
      value: leadValue,
      currency: 'BRL',
      predicted_ltv: leadValue * 12, // Valor estimado anual
      content_ids: ['contact_form'],
      contents: [{
        id: 'contact_form',
        quantity: 1,
        item_price: leadValue
      }],
      // Dados personalizados para segmentação
      custom_data: {
        segment: formData?.segment || 'not_specified',
        employees: formData?.employees || 'not_specified',
        lead_source: 'website_form'
      },
      ...formData
    });
    
    // Google Analytics - Enhanced Lead Tracking
    trackGAEvent('generate_lead', {
      event_category: 'lead_generation',
      event_label: 'contact_form_submission',
      value: leadValue,
      currency: 'BRL',
      lead_source: 'website_organic',
      segment: formData?.segment || 'not_specified',
      employees: formData?.employees || 'not_specified',
      revenue_bracket: formData?.revenue || 'not_specified',
      form_step: 'completed',
      engagement_score: leadValue > 5000 ? 'high_value' : leadValue > 2000 ? 'medium_value' : 'standard_value'
    });

    // Conversion Goal
    trackGAEvent('conversion', {
      send_to: 'G-MEQG32RXJD/lead_conversion',
      value: leadValue,
      currency: 'BRL'
    });

  }, [trackEvent, trackGAEvent]);

  const trackViewContent = useCallback((contentName: string) => {
    // Meta Pixel
    trackEvent('ViewContent', {
      content_name: contentName,
      content_type: 'page'
    });
    
    // Google Analytics - Enhanced Page Tracking
    trackGAEvent('page_view', {
      page_title: contentName,
      content_group1: 'lead_generation_site',
      content_group2: contentName.toLowerCase().replace(/\s+/g, '_'),
      engagement_time_msec: 1000
    });
    
    // Track funnel progression
    if (contentName.includes('funcionalidades') || contentName.includes('demonstração')) {
      trackGAEvent('funnel_step', {
        event_category: 'lead_funnel',
        event_label: 'interest_phase',
        step_name: contentName
      });
    }
  }, [trackEvent, trackGAEvent]);

  const trackInitiateCheckout = useCallback(() => {
    // Meta Pixel
    trackEvent('InitiateCheckout', {
      content_name: 'Demonstração Scale IA',
      content_category: 'Service'
    });
    
    // Google Analytics - Lead Intent Tracking
    trackGAEvent('begin_checkout', {
      event_category: 'lead_intent',
      event_label: 'demo_interest',
      engagement_score: 'high_intent'
    });
    
    // Funnel progression
    trackGAEvent('funnel_step', {
      event_category: 'lead_funnel', 
      event_label: 'consideration_phase',
      step_name: 'demo_request_intent'
    });
  }, [trackEvent, trackGAEvent]);

  const trackContact = useCallback(() => {
    // Meta Pixel
    trackCustomEvent('Contact', {
      event_source: 'website',
      action: 'contact_form_open'
    });
    
    // Google Analytics - Contact Intent
    trackGAEvent('contact', {
      event_category: 'lead_intent',
      event_label: 'form_open',
      engagement_score: 'very_high_intent'
    });
    
    // Funnel progression - Decision phase
    trackGAEvent('funnel_step', {
      event_category: 'lead_funnel',
      event_label: 'decision_phase', 
      step_name: 'contact_form_open'
    });
  }, [trackCustomEvent, trackGAEvent]);

  const trackFormSubmission = useCallback((formType: string) => {
    // Meta Pixel
    trackCustomEvent('FormSubmission', {
      form_type: formType,
      event_source: 'website'
    });
    
    // Google Analytics - Form Progression
    trackGAEvent('form_submit', {
      event_category: 'lead_generation',
      event_label: `${formType}_step`,
      form_step: formType === 'step1' ? 'basic_info' : 'company_info'
    });
  }, [trackCustomEvent, trackGAEvent]);

  // Novo: Trackear scroll depth para medir engajamento
  const trackScrollDepth = useCallback((depth: number) => {
    trackGAEvent('scroll', {
      event_category: 'engagement',
      event_label: `scroll_${depth}%`,
      value: depth
    });
  }, [trackGAEvent]);

  // Novo: Trackear tempo na página
  const trackTimeOnPage = useCallback((seconds: number) => {
    if (seconds > 0 && seconds % 30 === 0) { // A cada 30 segundos
      trackGAEvent('engagement_time', {
        event_category: 'engagement',
        event_label: 'time_on_page',
        value: seconds,
        engagement_quality: seconds > 120 ? 'high' : seconds > 60 ? 'medium' : 'low'
      });
    }
  }, [trackGAEvent]);

  return {
    trackEvent,
    trackCustomEvent,
    trackGAEvent,
    trackLead,
    trackViewContent,
    trackInitiateCheckout,
    trackContact,
    trackFormSubmission,
    trackScrollDepth,
    trackTimeOnPage
  };
};